import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { BRAND } from '@pdf-ticket/shared';
import { useEditorStore, type EditorTool } from '../lib/editor-store.js';
import { PdfViewer } from '../components/PdfViewer.js';
import { PageThumbnails } from '../components/PageThumbnails.js';
import { MetadataPanel } from '../components/MetadataPanel.js';
import { ShortcutsHelp } from '../components/ShortcutsHelp.js';
import { OutputPreview } from '../components/OutputPreview.js';
import { SearchPanel } from '../components/SearchPanel.js';
import { CommandPalette, type Command } from '../components/CommandPalette.js';
import { ShapeGallery } from '../components/ShapeGallery.js';
import './edit.css';

/** Cap on the recently-used shapes row in the gallery. */
const RECENT_SHAPES_MAX = 12;

/**
 * /edit — editor shell. Runs the LibPDF driver client-side: open a PDF,
 * inspect it (pages / fonts / flags), render pages with pdf.js (PdfViewer),
 * apply edits (text / shapes / images / markup / freehand / rotate / …),
 * then byte-preserving save + download.
 */

function download(bytes: Uint8Array, name: string): void {
  const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function EditRoute() {
  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const { fileName, doc, edits, activeTool, activeShapeType, saveResult, status, error, past, future } = useEditorStore();
  const openFile = useEditorStore((s) => s.openFile);
  const save = useEditorStore((s) => s.save);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const setPendingImage = useEditorStore((s) => s.setPendingImage);
  const setActiveMarkup = useEditorStore((s) => s.setActiveMarkup);
  const removeEdit = useEditorStore((s) => s.removeEdit);
  const clearEdits = useEditorStore((s) => s.clearEdits);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = past.length > 0;
  const canRedo = future.length > 0;
  const [helpOpen, setHelpOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Recently-used shapes (most-recent first), surfaced at the top of the gallery.
  const [recentShapes, setRecentShapes] = useState<string[]>([]);
  const pushRecentShape = (shapeType: string) =>
    setRecentShapes((prev) => [shapeType, ...prev.filter((s) => s !== shapeType)].slice(0, RECENT_SHAPES_MAX));

  // The text tool is available whenever a document is loaded. When a page has a
  // usable font, new text reuses it; otherwise the driver embeds a subset of the
  // bundled CJK fallback font for the typed glyphs (an intentional add — the
  // font-preservation rule only forbids rewriting UNEDITED content's fonts).
  const textDisabled = !doc;

  // Global keyboard: Esc returns to select; Ctrl/Cmd+Z undo,
  // Ctrl/Cmd+Shift+Z (or Ctrl+Y) redo. Skip when focus is in a text input so
  // metadata-field typing isn't hijacked.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const st = useEditorStore.getState();
      const mod = e.metaKey || e.ctrlKey;
      // Cmd/Ctrl+K → command palette (works regardless of focus / doc state).
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // Cmd/Ctrl+F → in-document search (overrides native find when a doc is open).
      if (mod && e.key.toLowerCase() === 'f' && st.doc) {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        if (paletteOpen) {
          setPaletteOpen(false);
          return;
        }
        if (searchOpen) {
          setSearchOpen(false);
          return;
        }
        if (st.activeTool !== 'select') {
          st.setActiveTool('select');
          return;
        }
        if (st.selectedEditIndices.length > 0) {
          st.selectEdit(null);
          return;
        }
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      // Delete / Backspace removes the current selection (single or multi).
      if ((e.key === 'Delete' || e.key === 'Backspace') && st.selectedEditIndices.length > 0) {
        e.preventDefault();
        st.removeEdits(st.selectedEditIndices);
        return;
      }
      // Cmd/Ctrl+A selects every spatial edit (shapes/lines/images/notes/text).
      if (mod && e.key.toLowerCase() === 'a' && st.doc) {
        const spatial = st.edits
          .map((ed, i) => ({ ed, i }))
          .filter(({ ed }) =>
            ['add-shape', 'add-line', 'add-image', 'add-highlight', 'add-annotation', 'add-text'].includes(ed.kind),
          )
          .map(({ i }) => i);
        if (spatial.length > 0) {
          e.preventDefault();
          st.setSelection(spatial);
          return;
        }
      }
      // Cmd/Ctrl+C copy · V paste · D duplicate (selection / clipboard based).
      if (mod && st.doc) {
        const k = e.key.toLowerCase();
        if (k === 'c' && st.selectedEditIndices.length > 0) {
          e.preventDefault();
          st.copySelection();
          return;
        }
        if (k === 'v' && st.clipboard.length > 0) {
          e.preventDefault();
          st.pasteClipboard();
          return;
        }
        if (k === 'd' && st.selectedEditIndices.length > 0) {
          e.preventDefault();
          st.duplicateSelection();
          return;
        }
      }
      // "?" (Shift+/) toggles the shortcuts overlay — no modifier required.
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        st.undo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        st.redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, paletteOpen]);

  // Re-clicking an already-active tool returns to select mode (toggle off),
  // matching common editor UX (Figma/Illustrator pattern).
  const pickTool = (t: EditorTool) =>
    setActiveTool(activeTool === t ? 'select' : t);

  async function onPick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const f = e.target.files?.[0];
    if (!f) return;
    await openFile(f.name, new Uint8Array(await f.arrayBuffer()));
    e.target.value = '';
  }

  // Drag-and-drop: a dropped .pdf file anywhere in the editor opens it.
  // Image drops on a *page* are handled in PdfViewer (different intent), so
  // we only act here when there's no doc yet, or when the drop is a PDF.
  const [dropArmed, setDropArmed] = useState(false);
  function onDragOver(e: React.DragEvent<HTMLDivElement>): void {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setDropArmed(true);
    }
  }
  function onDragLeave(e: React.DragEvent<HTMLDivElement>): void {
    if (e.currentTarget === e.target) setDropArmed(false);
  }
  async function onDrop(e: React.DragEvent<HTMLDivElement>): Promise<void> {
    setDropArmed(false);
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const pdf = [...files].find((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (!pdf) return;
    e.preventDefault();
    await openFile(pdf.name, new Uint8Array(await pdf.arrayBuffer()));
  }

  // "画像を挿入": pick a PNG/JPEG, read its bytes + natural size, then arm it for
  // placement (the next page drag drops it). The driver re-validates on save.
  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file || !/^image\/(png|jpeg)$/i.test(file.type)) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    let naturalWidth = 0;
    let naturalHeight = 0;
    try {
      const bmp = await createImageBitmap(new Blob([bytes as BlobPart]));
      naturalWidth = bmp.width;
      naturalHeight = bmp.height;
      bmp.close();
    } catch {
      // Aspect ratio falls back to 1:1 if the browser can't decode dimensions.
    }
    setPendingImage({ bytes, naturalWidth, naturalHeight, name: file.name });
  }

  async function onSample(): Promise<void> {
    const res = await fetch(`${import.meta.env.BASE_URL}sample.pdf`);
    if (!res.ok) return;
    await openFile('sample.pdf', new Uint8Array(await res.arrayBuffer()));
  }

  async function onSampleMulti(): Promise<void> {
    const { generateMultiPageSample } = await import('../lib/sample-multi.js');
    const bytes = await generateMultiPageSample();
    await openFile('sample-4pages.pdf', bytes);
  }

  async function onSampleRich(): Promise<void> {
    const { generateRichSample } = await import('../lib/sample-rich.js');
    const bytes = await generateRichSample();
    await openFile('sample-text.pdf', bytes);
  }

  async function onSave(): Promise<void> {
    await save();
    const r = useEditorStore.getState().saveResult;
    if (r) download(r.bytes, `${(fileName ?? 'document').replace(/\.pdf$/i, '')}.edited.pdf`);
  }

  // Command palette (Cmd/Ctrl+K) entries — context-gated by document state.
  const paletteCommands: Command[] = [
    { id: 'open', title: 'PDF を開く', hint: 'ファイル選択', run: () => fileInput.current?.click() },
    ...(doc
      ? [
          { id: 'save', title: '差分保存してダウンロード', run: () => void onSave() },
          ...(saveResult
            ? [{ id: 'preview', title: '保存結果をプレビュー', run: () => setPreviewOpen(true) }]
            : []),
          { id: 'search', title: 'ドキュメント内を検索', hint: '⌘F', run: () => setSearchOpen(true) },
          ...(canUndo ? [{ id: 'undo', title: '元に戻す', hint: '⌘Z', run: undo }] : []),
          ...(canRedo ? [{ id: 'redo', title: 'やり直し', hint: '⌘⇧Z', run: redo }] : []),
          { id: 'tool-select', title: 'ツール: 選択', run: () => setActiveTool('select') },
          { id: 'tool-note', title: 'ツール: 付箋', run: () => setActiveTool('note') },
          { id: 'tool-highlight', title: 'ツール: ハイライト', run: () => setActiveTool('highlight') },
          ...(!textDisabled
            ? [{ id: 'tool-text', title: 'ツール: テキスト', run: () => setActiveTool('text') }]
            : []),
          { id: 'tool-shape', title: '図形を挿入…', hint: 'PowerPoint 図形', run: () => setGalleryOpen(true) },
          { id: 'tool-image', title: '画像を挿入…', hint: 'PNG / JPEG', run: () => imageInput.current?.click() },
          { id: 'tool-markup', title: 'テキストにマーカー / 下線 / 取消線', hint: '文字をなぞる', run: () => setActiveMarkup('highlight') },
          { id: 'tool-freehand', title: '手書きで線を描く', hint: 'フリーハンド', run: () => setActiveTool('freehand') },
          ...(edits.length > 0 ? [{ id: 'clear', title: '編集をすべて取り消す', run: clearEdits }] : []),
        ]
      : [
          { id: 'sample1', title: 'サンプルを開く (1 ページ)', run: () => void onSample() },
          { id: 'sample4', title: 'サンプルを開く (4 ページ)', run: () => void onSampleMulti() },
          { id: 'sampleText', title: 'サンプルを開く (テキスト入り)', run: () => void onSampleRich() },
        ]),
    { id: 'help', title: 'キーボードショートカット', hint: '?', run: () => setHelpOpen(true) },
  ];

  return (
    <div
      className={`editor ${dropArmed ? 'editor--drop' : ''}`.trim()}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <header className="appbar">
        <span className="appbar__brand">{BRAND.name}</span>
        {fileName && <span className="appbar__file">{fileName}</span>}
        <span className="appbar__spacer" />
        {doc && (
          <>
            <button
              className="btn btn--icon"
              disabled={!canUndo}
              onClick={undo}
              title="元に戻す (Ctrl/Cmd+Z)"
              aria-label="元に戻す"
            >
              ↶
            </button>
            <button
              className="btn btn--icon"
              disabled={!canRedo}
              onClick={redo}
              title="やり直し (Ctrl/Cmd+Shift+Z)"
              aria-label="やり直し"
            >
              ↷
            </button>
            <button
              className="btn btn--icon"
              onClick={() => setSearchOpen(true)}
              title="ドキュメント内を検索 (Ctrl/Cmd+F)"
              aria-label="検索"
            >
              🔍
            </button>
          </>
        )}
        <button
          className="btn btn--icon"
          onClick={() => setHelpOpen(true)}
          title="キーボードショートカット (?)"
          aria-label="キーボードショートカット"
        >
          ?
        </button>
        <input ref={fileInput} type="file" accept="application/pdf,.pdf" hidden onChange={onPick} />
        <input ref={imageInput} type="file" accept="image/png,image/jpeg" hidden onChange={onPickImage} />
        <button className="btn" onClick={() => fileInput.current?.click()}>
          PDF を開く
        </button>
        <button className="btn btn--primary" disabled={!doc || status === 'saving'} onClick={onSave}>
          {status === 'saving' ? '保存中…' : '差分保存して DL'}
        </button>
      </header>

      <div className="editor__body">
        <nav className="rail" aria-label="ツール">
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'select'}
            title="選択 (クリックで配置しない)"
            onClick={() => setActiveTool('select')}
          >
            ▦
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'note'}
            title="付箋 — クリック位置に注釈を配置 (再クリックで解除 / Esc)"
            onClick={() => pickTool('note')}
          >
            ✎
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'highlight'}
            title="ハイライト — クリック位置に黄色の枠を配置 (再クリックで解除 / Esc)"
            onClick={() => pickTool('highlight')}
          >
            🖍
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'text'}
            title={textDisabled ? 'テキスト — PDF を開くと利用できます' : 'テキスト — クリック位置に文字を配置 (日本語可 / 再クリックで解除 / Esc)'}
            disabled={textDisabled}
            onClick={() => !textDisabled && pickTool('text')}
          >
            T
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'shape'}
            title={
              textDisabled
                ? '図形 — PDF を開くと利用できます'
                : activeTool === 'shape' && activeShapeType
                  ? `図形: ${activeShapeType} — ドラッグで配置 / 図形を変更`
                  : '図形 — PowerPoint 図形を挿入 (ドラッグで配置)'
            }
            disabled={textDisabled}
            onClick={() => !textDisabled && setGalleryOpen(true)}
          >
            ⬠
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'image'}
            title={
              textDisabled
                ? '画像 — PDF を開くと利用できます'
                : activeTool === 'image'
                  ? '画像: ドラッグで配置 / 別の画像は再選択'
                  : '画像を挿入 — PNG / JPEG を選んでドラッグで配置'
            }
            disabled={textDisabled}
            onClick={() => !textDisabled && imageInput.current?.click()}
          >
            🖼
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'markup'}
            title={
              textDisabled
                ? 'テキストマークアップ — PDF を開くと利用できます'
                : 'テキストマークアップ — マーカー / 下線 / 取消線（文字をなぞる）'
            }
            disabled={textDisabled}
            onClick={() => !textDisabled && setActiveMarkup(activeTool === 'markup' ? null : 'highlight')}
          >
            🖊
          </button>
          <button
            className="rail__btn"
            aria-pressed={activeTool === 'freehand'}
            title={
              textDisabled
                ? '手書き — PDF を開くと利用できます'
                : '手書き — フリーハンドで線を描く（ドラッグ）'
            }
            disabled={textDisabled}
            onClick={() => !textDisabled && setActiveTool(activeTool === 'freehand' ? 'select' : 'freehand')}
          >
            🖌
          </button>
        </nav>

        {doc && <PageThumbnails />}

        <section className="viewer" aria-label="ビューア">
          {doc ? (
            <PdfViewer />
          ) : (
            <div className="dropzone">
              <p>PDF を開いて編集を開始</p>
              <button className="btn" onClick={() => fileInput.current?.click()}>
                ファイルを選択
              </button>
              <button type="button" className="dropzone__sample" onClick={onSample}>
                サンプルを開く (1 ページ)
              </button>
              <button type="button" className="dropzone__sample" onClick={onSampleMulti}>
                サンプルを開く (4 ページ)
              </button>
              <button type="button" className="dropzone__sample" onClick={onSampleRich}>
                サンプルを開く (テキスト入り)
              </button>
              {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
            </div>
          )}
        </section>

        <aside className="inspector" aria-label="インスペクタ">
          {doc ? (
            <>
              <div className="insp__section">
                <h3>ドキュメント</h3>
                <div className="insp__row">
                  <span>バージョン</span>
                  <span>PDF {doc.version}</span>
                </div>
                <div className="insp__row">
                  <span>ページ</span>
                  <span>{doc.pageCount}</span>
                </div>
                {(['encrypted', 'signed', 'linearized'] as const).map((flag) => (
                  <div key={flag} className="insp__row">
                    <span>{flag}</span>
                    <span className={`flag ${doc[flag] ? 'flag--on' : 'flag--off'}`}>
                      {doc[flag] ? 'yes' : 'no'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="insp__section">
                <h3>フォント ({doc.fonts.length})</h3>
                {doc.fonts.length === 0 ? (
                  <div className="insp__row">
                    <span>なし</span>
                  </div>
                ) : (
                  doc.fonts.map((f, i) => (
                    <div key={i} className="insp__row">
                      <span>{f.ref.baseFont}</span>
                      <span>{f.ref.embedded ? '埋込' : '非埋込'}</span>
                    </div>
                  ))
                )}
              </div>

              <MetadataPanel />

              <div className="insp__section">
                <div className="insp__section-head">
                  <h3>編集ログ ({edits.length})</h3>
                  {edits.length > 0 && (
                    <button
                      type="button"
                      className="insp__section-action"
                      onClick={clearEdits}
                      title="編集をすべて取り消す"
                    >
                      全クリア
                    </button>
                  )}
                </div>
                {edits.map((e, i) => (
                  <div key={i} className="insp__row insp__row--editable">
                    <span>{e.kind}</span>
                    <span className="insp__row-tail">
                      <span>{'page' in e ? `p${e.page + 1}` : ''}</span>
                      <button
                        type="button"
                        className="insp__row-remove"
                        onClick={() => removeEdit(i)}
                        title="この編集を取り消す"
                        aria-label={`${e.kind} を取り消す`}
                      >
                        ✕
                      </button>
                    </span>
                  </div>
                ))}
              </div>

              {saveResult && (
                <div className="insp__section">
                  <h3>保存結果</h3>
                  <div className="insp__row">
                    <span>モード</span>
                    <span>{saveResult.mode}</span>
                  </div>
                  <div className="insp__row">
                    <span>原本保全</span>
                    <span>{saveResult.originalBytesPreserved ? '✓' : '✗'}</span>
                  </div>
                  <div className="insp__row">
                    <span>追加バイト</span>
                    <span>+{saveResult.bytesAdded}</span>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    style={{ marginTop: '0.6rem', width: '100%' }}
                    onClick={() => setPreviewOpen(true)}
                  >
                    プレビューを表示
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="insp__section">
              <h3>インスペクタ</h3>
              <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                PDF を開くと情報が表示されます。
              </p>
            </div>
          )}
        </aside>
      </div>

      <footer className="statusbar">
        <span>{fileName ?? 'ファイル未選択'}</span>
        {doc && <span>· {doc.pageCount} ページ</span>}
        <span>· {status}</span>
        <span className="statusbar__promise">フォント耐性 · 差分のみ保存</span>
      </footer>

      {searchOpen && doc && <SearchPanel onClose={() => setSearchOpen(false)} />}
      {previewOpen && saveResult && (
        <OutputPreview
          bytes={saveResult.bytes}
          fileName={`${(fileName ?? 'document').replace(/\.pdf$/i, '')}.edited.pdf`}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {paletteOpen && <CommandPalette commands={paletteCommands} onClose={() => setPaletteOpen(false)} />}
      <ShapeGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        recent={recentShapes}
        onPick={pushRecentShape}
      />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
